import React, { useState, useMemo, useEffect, Fragment } from 'react';
import { useMsal } from '@azure/msal-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { departments as allDepartments, menus as allMenus } from '../../config/menuConfig';
import { pathFor } from '../../config/routeMap';
import { getAccessPolicy, filterDepartments, filterMenus } from '../../config/accessControl';
import { Home, Activity, Scale, Truck, Globe2, Users, BarChart2, Layers, Database, Search, Bell, User, Settings, LogOut, Command, Pin, PinOff } from 'lucide-react';
import ThumbsupIcon from '../../assets/Thumbsup.png';
import ThumbsdownIcon from '../../assets/Thumbsdown.png';
import { subscribe as subNotifications, markRead as notifMarkRead, markAllRead as notifMarkAllRead, add as notifAdd } from '../../services/shared/notificationService';
import { getPrefs, updatePrefs, subscribe as subPrefs } from '../../services/shared/preferencesService';
import { subscribe as subAmScores, computeOverall as computeAmOverall } from '../../services/signal/signalScoreService';
import { subscribe as subFmScores, computeOverall as computeFmOverall } from '../../services/signal/fmSignalScoreService';
import { subscribe as subUbuntuScores, computeOverall as computeUbuntuOverall } from '../../services/signal/ubuntuSignalScoreService';
import CommandPalette from '../search/CommandPalette';

// Layout wrapper previously named Header – keep default export name for existing imports
export default function Header(){
  const [prefs, setPrefs] = React.useState(()=> getPrefs());
  React.useEffect(()=> subPrefs(setPrefs), []);
  const compact = !!prefs.compact;
  const autoThreshold = prefs.autoCollapseThreshold ?? 1100;
  const accordion = prefs.accordionDepartments !== false; // default true
  const setCompact = (v)=> updatePrefs({ compact: v });
  const [toast, setToast] = React.useState(null);
  const manualRef = React.useRef(false); // track manual toggle vs auto

  // responsive auto collapse (viewport < 1100px)
  React.useEffect(()=>{
    function onResize(){
      const shouldCompact = window.innerWidth < autoThreshold;
      if(shouldCompact && !compact){ updatePrefs({ compact:true }); }
      if(!shouldCompact && compact && !manualRef.current){ updatePrefs({ compact:false }); }
    }
    window.addEventListener('resize', onResize);
    onResize();
    return ()=> window.removeEventListener('resize', onResize);
  }, [compact, autoThreshold]);

  // toast auto hide
  React.useEffect(()=>{
    if(!toast) return; const t = setTimeout(()=> setToast(null), 4000); return ()=> clearTimeout(t);
  }, [toast]);
  // hotkey: Ctrl+Shift+C toggles compact
  React.useEffect(()=>{
    function onKey(e){
      if((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyC'){
        e.preventDefault(); manualRef.current = true; setCompact(!compact); setToast(!compact? 'Compact layout enabled' : 'Expanded layout restored');
      }
    }
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  }, [compact]);
  return (
    <div className={"relative h-screen w-full grid grid-rows-[56px_1fr] bg-slate-50 font-sans transition-[grid-template-columns] duration-300 " + (compact ? 'grid-cols-[72px_1fr]' : 'grid-cols-[260px_1fr]') }>
  <Sidebar compact={compact} accordion={accordion} />
      <Topbar
        compact={compact}
        setCompact={(v)=>{ manualRef.current = true; setCompact(v); setToast(v? 'Compact layout enabled':'Expanded layout restored'); }}
        threshold={autoThreshold}
        setThreshold={(val)=>{ updatePrefs({ autoCollapseThreshold: val }); setToast(`Auto-collapse < ${val}px`); }}
        accordion={accordion}
        setAccordion={(v)=> updatePrefs({ accordionDepartments: v })}
      />
      <main className="col-start-2 row-start-2 overflow-auto p-6">
        <Outlet />
      </main>
      {toast && (
        <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-50">
          <div className="group pointer-events-auto pl-4 pr-2 py-2 rounded-lg bg-slate-900/90 backdrop-blur text-white text-sm shadow-lg animate-in fade-in slide-in-from-top-2 flex items-center gap-3">
            <span>{toast}</span>
            <button onClick={()=> setToast(null)} className="text-slate-400 hover:text-white text-xs rounded p-1 focus:outline-none focus:ring-2 focus:ring-purple-500">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**************** Sidebar ****************/
function Sidebar({ compact, accordion }){
  const location = useLocation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  let account = instance.getActiveAccount?.();
  // if no active account but accounts available, set first as active for consistency
  if(!account){
    const all = instance.getAllAccounts?.() || [];
    if(all.length){ account = all[0]; instance.setActiveAccount?.(account); }
  }
  const displayName = account?.name || account?.idTokenClaims?.name || 'User';
  const email = account?.username || account?.idTokenClaims?.preferred_username || account?.idTokenClaims?.email || 'unknown@example.com';
  const rawRoles = account?.idTokenClaims?.roles || account?.idTokenClaims?.role || [];
  const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles].filter(Boolean);
  const primaryRole = roles[0] || '';
  const accessPolicy = useMemo(() => getAccessPolicy(email), [email]);
  const departments = useMemo(() => filterDepartments(allDepartments, accessPolicy), [accessPolicy]);
  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  // ----- Navigation Persistence & Accordion (Option 6) -----
  const prefsSnapshot = React.useRef(getPrefs()).current; // static snapshot for initial hydration
  const [independent, setIndependent] = React.useState(()=> new Set(Array.isArray(prefsSnapshot.independentDepartments) ? prefsSnapshot.independentDepartments : []));
  // persist independent set when it changes
  useEffect(()=>{ updatePrefs({ independentDepartments: Array.from(independent) }); }, [independent]);
  const isIndependent = (dept)=> independent.has(dept);
  const toggleIndependent = (dept)=>{
    setIndependent(prev=>{
      const next = new Set(prev);
      if(next.has(dept)) next.delete(dept); else next.add(dept);
      return next;
    });
    // If converting to accordion participant while accordion is enabled & multiple accordion participants are open, enforce
    setOpen(prev=>{
      if(!accordion) return prev;
      const after = new Set(prev);
      const nonIndependent = Array.from(after).filter(d=> ! (independent.has(d) ^ (d===dept && independent.has(dept)))); // approximate; enforcement handled in effect
      return after; // enforcement handled in effect below
    });
  };

  // initial open departments: prefer persisted; else derive from path
  const initialOpen = useMemo(()=>{
    const persisted = prefsSnapshot.openDepartments;
    if(Array.isArray(persisted) && persisted.length){
      if(accordion){
        // keep all independent ones plus (active or first) accordion participant
        const activeDept = departments.find(d=> location.pathname.startsWith(pathFor(d)));
        const indeps = persisted.filter(d=> isIndependent(d));
        const accordionCandidates = persisted.filter(d=> !isIndependent(d));
        const chosen = activeDept && !isIndependent(activeDept) && accordionCandidates.includes(activeDept)
          ? activeDept
          : accordionCandidates[0];
        return new Set([...indeps, chosen].filter(Boolean));
      }
      return new Set(persisted.filter(d=> departments.includes(d)));
    }
    // Start with all departments collapsed on page load
    return new Set();
  }, [location.pathname, accordion, prefsSnapshot.openDepartments, independent]);
  const [open, setOpen] = useState(initialOpen);

  // State for submenu collapse/expand (start collapsed on page load)
  const [submenuOpen, setSubmenuOpen] = useState(()=>{
    // Start with all submenus collapsed on page load
    return new Set();
  });
  // Track items user explicitly collapsed so auto-open won't fight user intent
  const userCollapsedRef = React.useRef(new Set());
  // Track departments user explicitly collapsed (needed because location keeps matching their path)
  const userCollapsedDeptRef = React.useRef(new Set());

  // ensure active dept auto-opens (respect user-collapsed + accordion)
  // and collapse when navigating away from a department
  useEffect(()=>{
    const activeDept = departments.find(d=> location.pathname.startsWith(pathFor(d)));
    
    // Auto-open active department if not already open and not user-collapsed
    if(activeDept && !open.has(activeDept) && !userCollapsedDeptRef.current.has(activeDept)){
      setOpen(prev=>{
        if(accordion && !isIndependent(activeDept)){
          // keep independent ones open, replace other accordion participants
          const indeps = Array.from(prev).filter(d=> isIndependent(d));
          return new Set([...indeps, activeDept]);
        }
        const next = new Set(prev); next.add(activeDept); return next;
      });
    }
    
    // Auto-collapse departments that are no longer active (unless pinned/independent)
    setOpen(prev=>{
      const next = new Set();
      Array.from(prev).forEach(dept => {
        // Keep if: independent/pinned, OR is the active department
        if(isIndependent(dept) || dept === activeDept){
          next.add(dept);
        }
        // If user manually expanded it (not in userCollapsed set) we'll let it stay open
        // This gives users control but still auto-expands the active one
      });
      return next;
    });
    
    // Auto-open submenus for current path unless user explicitly collapsed that submenu
    if(activeDept && allMenus[activeDept]){
      const visibleMenus = filterMenus(activeDept, allMenus[activeDept], accessPolicy);
      Object.keys(visibleMenus).forEach(submenuLabel => {
        const submenuPath = pathFor(activeDept, submenuLabel);
        if(location.pathname.startsWith(submenuPath)){
          const key = activeDept + '-' + submenuLabel;
          if(!userCollapsedRef.current.has(key)){
            setSubmenuOpen(prev=>{ if(prev.has(key)) return prev; const next = new Set(prev); next.add(key); return next; });
          }
        }
      });
    }
    
    // Auto-collapse submenus that are not in the current path
    setSubmenuOpen(prev=>{
      const next = new Set();
      Array.from(prev).forEach(key => {
        // Extract dept from key (format: dept-submenu or dept-submenu-...)
        const keyParts = key.split('-');
        const dept = keyParts[0];
        
        // Keep if it's in the active department and matches current path
        if(activeDept && dept === activeDept && allMenus[activeDept]){
          const visMenus = filterMenus(activeDept, allMenus[activeDept], accessPolicy);
          let shouldKeep = false;
          Object.keys(visMenus).forEach(submenuLabel => {
            const submenuPath = pathFor(activeDept, submenuLabel);
            const expectedKey = activeDept + '-' + submenuLabel;
            if(key.startsWith(expectedKey) && location.pathname.startsWith(submenuPath)){
              shouldKeep = true;
            }
          });
          if(shouldKeep){
            next.add(key);
          }
        }
      });
      return next;
    });
  }, [location.pathname, accordion, independent]);

  // enforce accordion invariant if user toggles setting on with multiple open
  useEffect(()=>{
    if(!accordion) return;
    const accordionOpen = Array.from(open).filter(d=> !isIndependent(d));
    if(accordionOpen.length <= 1) return;
    const activeDept = departments.find(d=> accordionOpen.includes(d) && location.pathname.startsWith(pathFor(d)));
    const keep = activeDept || accordionOpen[0];
    setOpen(prev=> new Set([...Array.from(prev).filter(d=> isIndependent(d)), keep]));
  }, [accordion, independent]);

  // persist open state whenever it changes (serialize sets)
  useEffect(()=>{
    updatePrefs({
      openDepartments: Array.from(open),
      openSubmenus: Array.from(submenuOpen)
    });
  }, [open, submenuOpen]);

  const [amOverall, setAmOverall] = React.useState(null);
  const [fmOverall, setFmOverall] = React.useState(null);
  const [ubuntuOverall, setUbuntuOverall] = React.useState(null);
  React.useEffect(()=>{
    const unsubA = subAmScores((s)=> setAmOverall(computeAmOverall(s)));
    const unsubF = subFmScores((s)=> setFmOverall(computeFmOverall(s)));
    const unsubU = subUbuntuScores((s)=> setUbuntuOverall(computeUbuntuOverall(s)));
    return ()=> { unsubA?.(); unsubF?.(); unsubU?.(); };
  }, []);

  const smallBadge = (label)=>{
    const style = 'ml-2 px-1.5 h-4 inline-flex items-center rounded-full border text-[10px] font-semibold';
    if(label==='no-go') return <span className={style + ' bg-rose-100/70 border-rose-200 text-rose-700'}><img src={ThumbsdownIcon} alt="👎" className="h-3 w-3 inline" /></span>;
    if(label==='cautious') return <span className={style + ' bg-amber-100/70 border-amber-200 text-amber-800'}><img src={ThumbsupIcon} alt="👍" className="h-3 w-3 inline" /></span>;
    if(label==='go') return <span className={style + ' bg-emerald-100/70 border-emerald-200 text-emerald-700'}><img src={ThumbsupIcon} alt="👍" className="h-3 w-3 inline" /><img src={ThumbsupIcon} alt="👍" className="h-3 w-3 inline" /></span>;
    return null;
  };

  const trustSignalLabel = (
    <span className="truncate flex-1 flex items-center">
      Trust Signal
      {!compact && (<span className="ml-1 flex items-center gap-1">{smallBadge(amOverall)}{smallBadge(fmOverall)}{smallBadge(ubuntuOverall)}</span>)}
    </span>
  );

  const iconFor = (dept)=>{
    const map = {
      'Trust Signal':   <Activity size={18}/>,
      'Bread':          <Layers size={18}/>,
      'Ethics':         <Scale size={18}/>,
      'Data':           <Database size={18}/>,
      'KRO Exchange':   <Globe2 size={18}/>,
      'Settlement Hub': <BarChart2 size={18}/>,
      // Legacy mappings for backward compatibility
      'Experience Management': <Home size={18}/>,
      'Legal & Policy': <Scale size={18}/>,
      'Supply Chain': <Truck size={18}/>,
      'Cultural & Global': <Globe2 size={18}/>,
      'Supplier Ecosystem': <Users size={18}/>,
      'Investor Market': <BarChart2 size={18}/>,
      'Experience Enablement': <Layers size={18}/>,
      'Data Analytics': <Database size={18}/>,
      'Trust Tech Scan': <Activity size={18}/> };
    return map[dept] || <Layers size={18}/>;
  };

  const toggle = (dept)=>{
    setOpen(prev=>{
      if(prev.has(dept)){
        const next = new Set(prev); 
        next.delete(dept); 
        userCollapsedDeptRef.current.add(dept); 
        return next;
      }
      // opening
      userCollapsedDeptRef.current.delete(dept);
      if(accordion && !isIndependent(dept)){
        const indeps = Array.from(prev).filter(d=> isIndependent(d));
        return new Set([...indeps, dept]);
      }
      const next = new Set(prev); 
      next.add(dept); 
      return next;
    });
  };

  const toggleSubmenu = (submenuKey)=>{
    setSubmenuOpen(prev=>{
      const next = new Set(prev);
      if(next.has(submenuKey)){
        next.delete(submenuKey);
        userCollapsedRef.current.add(submenuKey);
      } else {
        next.add(submenuKey);
        userCollapsedRef.current.delete(submenuKey);
      }
      return next;
    });
  };

  const isActivePath = (p)=> location.pathname === p || location.pathname.startsWith(p + '/');

  const renderChildren = (dept, obj, level=1, trail=[])=>{
    return Object.entries(obj).map(([label, children], idx)=>{
      const currentTrail = [...trail, label];
      const targetPath = pathFor(dept, ...currentTrail.map(t=> t === 'Overview' ? '' : t).filter(Boolean));
      const active = isActivePath(targetPath);
      const hasChildren = Array.isArray(children) && children.length > 0;
      const submenuKey = dept + '-' + currentTrail.join('-');
      const isSubmenuOpen = submenuOpen.has(submenuKey);
      const itemKey = dept + '-' + currentTrail.join('-') + idx;

      return (
        <Fragment key={itemKey}>
          <div className="relative group">
            <button
              onClick={()=> navigate(targetPath)}
              className={[
                'w-full text-left flex items-center gap-2 rounded-lg px-3 py-1.5 text-[13px] transition-colors',
                active ? 'bg-white/15 text-white' : 'text-white/85 hover:bg-white/10'
              ].join(' ')}
              style={{paddingLeft: level*14}}
              aria-haspopup={hasChildren? 'tree' : undefined}
              aria-expanded={hasChildren? isSubmenuOpen : undefined}
            >
              <span className="flex-1 truncate">{label}</span>
              {hasChildren && (
                <span className="shrink-0">
                  <button
                    type="button"
                    aria-label={isSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
                    onClick={(e)=> { e.stopPropagation(); toggleSubmenu(submenuKey); }}
                    className={[
                      'w-5 h-5 grid place-items-center rounded-md text-xs text-white/60 hover:text-white/90 transition-transform',
                      isSubmenuOpen ? 'rotate-90' : ''
                    ].join(' ')}
                  >›</button>
                </span>
              )}
            </button>
          </div>
          {hasChildren && isSubmenuOpen && (
            <div className="mt-0.5" role="group" aria-label={label + ' submenu'}>
              {children.map((child,i)=>{
                if(typeof child === 'string'){
                  const childPath = pathFor(dept, ...currentTrail, child);
                  const childActive = isActivePath(childPath);
                  return (
                    <button
                      key={itemKey+'-'+i}
                      onClick={()=> navigate(childPath)}
                      className={[
                        'w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-[12px] ml-px',
                        childActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'
                      ].join(' ')}
                      style={{paddingLeft:(level+1)*14}}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      <span className="truncate">{child}</span>
                    </button>
                  );
                } else if(typeof child === 'object' && child){
                  const sub = child;
                  const childPath = pathFor(dept, ...currentTrail, sub.label);
                  const subActive = isActivePath(childPath);
                  const subSubmenuKey = dept + '-' + currentTrail.join('-') + '-' + sub.label;
                  const isSubSubmenuOpen = submenuOpen.has(subSubmenuKey);
                  const hasSubChildren = Array.isArray(sub.children) && sub.children.length > 0;
                  return (
                    <div key={itemKey+'-grp-'+i} className="mt-1">
                      <div className="relative">
                        <button
                          onClick={()=> navigate(childPath)}
                          className={[
                            'w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-[12px]',
                            subActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'
                          ].join(' ')}
                          style={{paddingLeft:(level+1)*14}}
                          aria-haspopup={hasSubChildren? 'group' : undefined}
                          aria-expanded={hasSubChildren? isSubSubmenuOpen : undefined}
                        >
                          <span className="h-1.5 w-1.5 rounded-sm bg-current" />
                          <span className="truncate flex-1">{sub.label}</span>
                          {hasSubChildren && (
                            <button
                              type="button"
                              aria-label={isSubSubmenuOpen ? 'Collapse submenu' : 'Expand submenu'}
                              onClick={(e)=> { e.stopPropagation(); toggleSubmenu(subSubmenuKey); }}
                              className={[
                                'w-5 h-5 grid place-items-center rounded-md text-xs text-white/60 hover:text-white/90 transition-transform',
                                isSubSubmenuOpen ? 'rotate-90' : ''
                              ].join(' ')}
                            >›</button>
                          )}
                        </button>
                      </div>
                      {hasSubChildren && isSubSubmenuOpen && (
                        <div className="mt-0.5" role="group" aria-label={sub.label + ' submenu'}>
                          {sub.children.map((leaf,j)=>{
                            const leafPath = pathFor(dept, ...currentTrail, sub.label, leaf);
                            const leafActive = isActivePath(leafPath);
                            return (
                              <button
                                key={itemKey+'-leaf-'+j}
                                onClick={()=> navigate(leafPath)}
                                className={[
                                  'w-full flex items-center gap-2 rounded px-3 py-1.5 text-[11px]',
                                  leafActive ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/10'
                                ].join(' ')}
                                style={{paddingLeft:(level+2)*14}}
                              >
                                <span className="h-1 w-1 rounded-full bg-current" />
                                <span className="truncate">{leaf}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
        </Fragment>
      );
    });
  };

  return (
    <aside className={"col-start-1 row-span-2 bg-gradient-to-b from-purple-900 to-purple-800 text-white flex flex-col " + (compact ? 'items-center' : '')}>
      <div className={"h-14 w-full flex items-center border-b border-white/10 " + (compact ? 'justify-center' : 'px-4 gap-2') }>
        <div className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center text-[10px] font-bold">K‑R‑O</div>
        {!compact && <div className="font-semibold text-sm tracking-wide">Trust Tech Studio™</div>}
      </div>
      <nav className="flex-1 overflow-auto py-3">
        {departments.map((dept)=>{
          const deptRoot = pathFor(dept);
          const deptActive = isActivePath(deptRoot);
          const openDept = open.has(dept);
          const content = filterMenus(dept, allMenus[dept], accessPolicy);
          return (
            <div key={dept} className="mb-2">
              {/* Department Row */}
              <div className={[
                'relative w-full flex items-center rounded-xl text-sm transition-colors group',
                deptActive ? 'bg-white/15 ring-1 ring-white/20 text-white' : 'text-white/85 hover:bg-white/10',
                compact ? 'justify-center px-2 py-2' : 'px-3 py-2'
              ].join(' ')}>
                <button
                  onClick={()=> navigate(deptRoot)}
                  className={['flex items-center', compact ? '' : 'flex-1 gap-2 text-left'] .join(' ')}
                  aria-label={compact ? dept : undefined}
                >
                  {iconFor(dept)}
                  {!compact && (dept==='Trust Signal' ? (
                    <span className="truncate flex-1 flex items-center">Trust Signal<span className="ml-1 flex items-center gap-1">{smallBadge(amOverall)}{smallBadge(fmOverall)}</span></span>
                  ) : (
                    <span className="truncate flex-1">{dept}</span>
                  ))}
                </button>
                {!compact && (
                  <button
                    type="button"
                    onClick={()=> toggleIndependent(dept)}
                    aria-label={isIndependent(dept) ? `Unpin ${dept} (participate in accordion)` : `Pin ${dept} (keep open)`}
                    className={[
                      'ml-1 w-6 h-6 grid place-items-center rounded-md text-xs transition-colors',
                      isIndependent(dept) ? 'text-amber-300 hover:text-amber-200' : 'text-white/40 hover:text-white/80'
                    ].join(' ')}
                    title={isIndependent(dept) ? 'Pinned: stays open' : 'Pin to keep open'}
                  >
                    {isIndependent(dept) ? <Pin size={16} /> : <PinOff size={16} />}
                  </button>
                )}
                {/* Chevron toggle (separate to avoid forced navigation) */}
                {!compact && (
                  <button
                    type="button"
                    aria-label={openDept ? `Collapse ${dept}` : `Expand ${dept}`}
                    aria-expanded={openDept}
                    onClick={(e)=> {
                      e.stopPropagation();
                      console.log(`Toggling ${dept}, currently open: ${openDept}`);
                      toggle(dept);
                    }}
                    className={[
                      'ml-1 w-6 h-6 grid place-items-center rounded-md text-xs text-white/60 hover:text-white/90 transition-transform',
                      openDept ? 'rotate-90' : ''
                    ].join(' ')}
                  >›</button>
                )}
                {compact && <DelayedTooltip label={dept} />}
              </div>
              {openDept && content && !compact && (
                <div className="mt-1 space-y-1 transition-all duration-200 ease-in-out">{renderChildren(dept, content)}</div>
              )}
            </div>
          );
        })}
      </nav>
  {!compact && account && (
        <div className="border-t border-white/10 p-3 text-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center"><User size={16}/></div>
            <div>
      <div className="font-semibold leading-4 truncate max-w-[140px]" title={displayName}>{displayName}</div>
      <div className="text-white/70 text-[11px] truncate max-w-[140px]" title={email}>{email}</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button className="flex items-center gap-1 text-left px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15"><Settings size={14}/> Settings</button>
            <button onClick={handleLogout} className="flex items-center gap-1 text-left px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15"><LogOut size={14}/> Logout</button>
          </div>
        </div>
      )}
    </aside>
  );
}

/**************** Topbar ****************/
function Topbar({ compact, setCompact, threshold, setThreshold, accordion, setAccordion }){
  const [open, setOpen] = React.useState(false); // command palette
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [amScores, setAmScores] = React.useState({});
  const [amOverall, setAmOverall] = React.useState(null);
  const [fmScores, setFmScores] = React.useState({});
  const [fmOverall, setFmOverall] = React.useState(null);
  const [ubuntuOverall, setUbuntuOverall] = React.useState(null);
  const profileRef = React.useRef(null);
  const notifRef = React.useRef(null);
  const navigate = useNavigate();
  const { instance } = useMsal();
  let account = instance.getActiveAccount?.();
  if(!account){
    const all = instance.getAllAccounts?.() || [];
    if(all.length){ account = all[0]; instance.setActiveAccount?.(account); }
  }
  const displayName = account?.name || account?.idTokenClaims?.name || 'User';
  const email = account?.username || account?.idTokenClaims?.preferred_username || account?.idTokenClaims?.email || 'unknown@example.com';
  const rawRoles = account?.idTokenClaims?.roles || account?.idTokenClaims?.role || [];
  const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles].filter(Boolean);
  const primaryRole = roles[0] || 'Member';
  const topbarAccessPolicy = useMemo(() => getAccessPolicy(email), [email]);
  const filteredDepts = useMemo(() => filterDepartments(allDepartments, topbarAccessPolicy), [topbarAccessPolicy]);
  const filteredMenusMap = useMemo(() => {
    const map = {};
    filteredDepts.forEach(d => { map[d] = filterMenus(d, allMenus[d], topbarAccessPolicy); });
    return map;
  }, [filteredDepts, topbarAccessPolicy]);
  const initials = displayName.split(/\s+/).slice(0,2).map(p=> p[0]).join('').toUpperCase();
  const handleLogout = () => {
    setProfileOpen(false);
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  React.useEffect(()=>{
    function onGlobal(e){
      if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k'){
        e.preventDefault(); setOpen(o=> !o);
      } else if(e.key === '/' && (e.target === document.body || e.target.tagName === 'INPUT' && e.target.value === '')){
        // open on lone '/' not inside a filled input
        if(!open){ e.preventDefault(); setOpen(true);} }
  else if(e.key === 'Escape'){ setOpen(false); setProfileOpen(false); setNotifOpen(false); }
    }
    window.addEventListener('keydown', onGlobal);
    return ()=> window.removeEventListener('keydown', onGlobal);
  }, [open]);

  // close on outside click for profile menu
  React.useEffect(()=>{
    if(!profileOpen) return;
    function onClick(e){
      if(profileRef.current && !profileRef.current.contains(e.target)){
        setProfileOpen(false);
      }
    }
    window.addEventListener('mousedown', onClick);
    return ()=> window.removeEventListener('mousedown', onClick);
  }, [profileOpen]);

  // close notification dropdown on outside click
  React.useEffect(()=>{
    if(!notifOpen) return;
    function onClick(e){
      if(notifRef.current && !notifRef.current.contains(e.target)){
        setNotifOpen(false);
      }
    }
    window.addEventListener('mousedown', onClick);
    return ()=> window.removeEventListener('mousedown', onClick);
  }, [notifOpen]);

  // subscribe to notifications
  React.useEffect(()=>{
    const unsub = subNotifications(list=> setNotifications(list));
    return unsub;
  }, []);

  React.useEffect(()=>{
    let prev = null;
    const unsub = subAmScores((scores)=>{
      setAmScores(scores);
      const next = computeAmOverall(scores);
      setAmOverall(next);
      if(prev && prev !== 'no-go' && next === 'no-go'){
        notifAdd({ title:'AM Status: No-Go', body:'Overall AM signal moved to NO/GO. Investigate immediately.' });
      }
      prev = next;
    });
    return unsub;
  }, []);

  React.useEffect(()=>{
    let prev = null;
    const unsub = subFmScores((scores)=>{
      setFmScores(scores);
      const next = computeFmOverall(scores);
      setFmOverall(next);
      if(prev && prev !== 'no-go' && next === 'no-go'){
        notifAdd({ title:'FM Status: NO/GO', body:'Overall FM signal moved to NO/GO. Investigate immediately.' });
      }
      prev = next;
    });
    return unsub;
  }, []);

  React.useEffect(()=>{
    let prev = null;
    const unsub = subUbuntuScores((scores)=>{
      const next = computeUbuntuOverall(scores);
      setUbuntuOverall(next);
      if(prev && prev !== 'no-go' && next === 'no-go'){
        notifAdd({ title:'Ubuntu Status: NO/GO', body:'Overall Ubuntu signal moved to NO/GO. Investigate immediately.' });
      }
      prev = next;
    });
    return unsub;
  }, []);

  const unread = notifications.filter(n=> !n.read).length;
  const sorted = notifications.slice().sort((a,b)=> b.ts - a.ts);
  const formatTime = (ts)=>{
    const diff = Date.now() - ts;
    const m = Math.floor(diff/60000);
    if(m < 1) return 'just now';
    if(m < 60) return m + 'm';
    const h = Math.floor(m/60);
    if(h < 24) return h + 'h';
    const d = Math.floor(h/24); return d + 'd';
  };

  const openNotif = ()=> setNotifOpen(o=> !o);
  const onMarkRead = (id)=> notifMarkRead(id);
  const onMarkAll = ()=> notifMarkAllRead();

  const onProfileKeyDown = (e)=>{
    if(e.key === 'Escape'){ setProfileOpen(false); e.stopPropagation(); }
    if(e.key === 'ArrowDown' || e.key === 'ArrowUp'){
      e.preventDefault();
      const items = profileRef.current?.querySelectorAll('[data-profile-item]');
      if(!items || !items.length) return;
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const focusable = Array.from(items);
      const idx = focusable.indexOf(document.activeElement);
      const next = idx === -1 ? (dir === 1 ? 0 : focusable.length -1) : (idx + dir + focusable.length) % focusable.length;
      focusable[next].focus();
    }
  };

  const profileActions = {
    profile: ()=> { setProfileOpen(false); navigate('/profile'); },
    settings: ()=> { setProfileOpen(false); navigate('/settings'); },
    logout: handleLogout
  };

  const amBadge = (()=>{
    const base = 'inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-semibold border';
    if(amOverall === 'no-go') return <span className={base + ' bg-rose-100 border-rose-200 text-rose-700'}>AM: <img src={ThumbsdownIcon} alt="👎" className="h-3.5 w-3.5 inline" /></span>;
    if(amOverall === 'cautious') return <span className={base + ' bg-amber-100 border-amber-200 text-amber-800'}>AM: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
    if(amOverall === 'go') return <span className={base + ' bg-emerald-100 border-emerald-200 text-emerald-700'}>AM: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /><img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
    return <span className={base + ' bg-slate-100 border-slate-200 text-slate-600'}>AM: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
  })();
  const fmBadge = (()=>{
    const base = 'inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-semibold border';
    if(fmOverall === 'no-go') return <span className={base + ' bg-rose-100 border-rose-200 text-rose-700'}>FM: <img src={ThumbsdownIcon} alt="👎" className="h-3.5 w-3.5 inline" /></span>;
    if(fmOverall === 'cautious') return <span className={base + ' bg-amber-100 border-amber-200 text-amber-800'}>FM: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
    if(fmOverall === 'go') return <span className={base + ' bg-emerald-100 border-emerald-200 text-emerald-700'}>FM: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /><img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
    return <span className={base + ' bg-slate-100 border-slate-200 text-slate-600'}>FM: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
  })();
  const ubuntuBadge = (()=>{
    const base = 'inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-semibold border';
    if(ubuntuOverall === 'no-go') return <span className={base + ' bg-rose-100 border-rose-200 text-rose-700'}>UB: <img src={ThumbsdownIcon} alt="👎" className="h-3.5 w-3.5 inline" /></span>;
    if(ubuntuOverall === 'cautious') return <span className={base + ' bg-amber-100 border-amber-200 text-amber-800'}>UB: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
    if(ubuntuOverall === 'go') return <span className={base + ' bg-emerald-100 border-emerald-200 text-emerald-700'}>UB: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /><img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
    return <span className={base + ' bg-slate-100 border-slate-200 text-slate-600'}>UB: <img src={ThumbsupIcon} alt="👍" className="h-3.5 w-3.5 inline" /></span>;
  })();

  return (
    <header className="col-start-2 row-start-1 h-14 bg-gradient-to-r from-purple-700 to-fuchsia-600 border-b border-purple-800 flex items-center justify-between px-4 text-white">
      <div className="flex items-center gap-3 text-sm text-white/80">
        <div>
          <span className="font-semibold text-white">Trust Tech Studio™</span>
          <span className="mx-2 text-white/50">›</span>
          <span className="text-white/90">Dashboard</span>
        </div>
        <div className="ml-2 flex items-center gap-2">{amBadge}{fmBadge}{ubuntuBadge}</div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={()=> setOpen(true)}
          className="hidden md:flex items-center gap-2 bg-white/10 border border-white/20 hover:border-white/30 hover:bg-white/15 text-white/80 rounded-lg pl-2 pr-3 h-9 transition-colors"
        >
          <Search size={16}/>
          <span className="text-sm text-white/70">Search…</span>
          <span className="ml-6 flex items-center gap-1 text-[10px] font-medium text-white/60">
            <span className="px-1 py-0.5 rounded bg-white/15 border border-white/20 flex items-center gap-0.5"><Command size={10}/>K</span>
            <span className="px-1 py-0.5 rounded bg-white/15 border border-white/20">/</span>
          </span>
        </button>
        <button className="md:hidden w-9 h-9 grid place-items-center bg-white/10 border border-white/20 text-white/80 rounded-lg hover:bg-white/15" onClick={()=> setOpen(true)}>
          <Search size={16}/>
        </button>
        <div className="relative" ref={notifRef}>
          <button
            aria-haspopup="menu"
            aria-expanded={notifOpen}
            onClick={openNotif}
            className={["relative w-9 h-9 grid place-items-center bg-white/10 border border-white/20 text-white/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/60 hover:bg-white/15", notifOpen? 'ring-2 ring-white/70':''].join(' ')}>
            <Bell size={16}/>
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 text-[10px] grid place-items-center rounded-full bg-rose-600 text-white">{unread > 9 ? '9+' : unread}</span>
            )}
          </button>
          {notifOpen && (
            <div role="menu" className="absolute right-0 mt-2 w-96 max-h-[480px] flex flex-col rounded-lg bg-white border border-slate-200 shadow-lg z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 h-10 border-b border-slate-100 bg-slate-50/50">
                <span className="text-sm font-medium text-slate-700">Notifications</span>
                <div className="flex items-center gap-2">
                  <button onClick={()=> notifAdd({ title:'Test Event', body:'Generated demo notification.' })} className="text-[11px] text-purple-600 hover:text-purple-500">Add</button>
                  {unread > 0 && <button onClick={onMarkAll} className="text-[11px] text-slate-500 hover:text-slate-700">Mark all read</button>}
                  <button onClick={()=> setNotifOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Close">×</button>
                </div>
              </div>
              {sorted.length === 0 ? (
                <div className="flex-1 grid place-items-center p-10 text-slate-400 text-sm">No notifications</div>
              ) : (
                <ul className="flex-1 overflow-auto divide-y divide-slate-100">
                  {sorted.map(n=> (
                    <li key={n.id} className={["px-4 py-3 text-sm flex flex-col gap-1", n.read? 'bg-white':'bg-purple-50/60'].join(' ')}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{n.title}</p>
                          <p className="text-slate-600 text-[12px] leading-snug line-clamp-3">{n.body}</p>
                        </div>
                        <div className="text-[11px] text-slate-400 flex flex-col items-end gap-2">
                          <span>{formatTime(n.ts)}</span>
                          {!n.read && <button onClick={()=> onMarkRead(n.id)} className="text-[10px] text-purple-600 hover:text-purple-500">Mark read</button>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="h-9 px-4 flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50">
                <button onClick={()=> notifAdd({ title:'Signal Refresh', body:'New trust signal ingest completed.' })} className="text-[11px] text-purple-600 hover:text-purple-500">Simulate Signal</button>
                <button onClick={()=> notifAdd({ title:'Report Generated', body:'Integrity report v2 ready for review.' })} className="text-[11px] text-purple-600 hover:text-purple-500">Simulate Report</button>
              </div>
            </div>
          )}
        </div>
        <div className="relative" ref={profileRef}>
          <button
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onClick={()=> setProfileOpen(o=> !o)}
            onKeyDown={(e)=>{ if(e.key==='ArrowDown'){ e.preventDefault(); setProfileOpen(true); setTimeout(()=>{ profileRef.current?.querySelector('[data-profile-item]')?.focus(); },0);} }}
            className={["w-9 h-9 grid place-items-center bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-semibold text-xs", profileOpen ? 'ring-2 ring-purple-500' : ''].join(' ')}
          >
            {initials}
          </button>
          {profileOpen && (
            <div
              role="menu"
              tabIndex={-1}
              onKeyDown={onProfileKeyDown}
              className="absolute right-0 mt-2 w-56 rounded-lg bg-white border border-slate-200 shadow-lg py-1 z-50 animate-in fade-in zoom-in"
            >
              <div className="px-4 py-3 border-b border-slate-100 flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white grid place-items-center font-semibold text-sm">{initials}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate" title={displayName}>{displayName}</p>
                  <p className="text-xs text-slate-500 truncate" title={email}>{email}</p>
                  <span className="inline-flex items-center mt-1 px-2 h-5 rounded-full bg-purple-100 text-[10px] font-medium text-purple-700 border border-purple-200">{primaryRole}</span>
                </div>
              </div>
              <button data-profile-item onClick={()=> setCompact(!compact)} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:bg-slate-100 focus:outline-none" role="menuitem">{compact ? 'Expand Layout' : 'Compact Layout'}</button>
              <div className="px-4 pt-2 pb-3 border-b border-slate-100">
                <label className="flex items-center justify-between text-[11px] font-medium text-slate-600 mb-1">
                  <span>Auto collapse width</span>
                  <span className="tabular-nums text-slate-500">{threshold}px</span>
                </label>
                <input
                  type="range"
                  min={800}
                  max={1600}
                  step={10}
                  value={threshold}
                  onChange={(e)=> setThreshold(Number(e.target.value))}
                  className="w-full accent-purple-600 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>800</span><span>1600</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[11px] font-medium text-slate-600">Accordion nav</span>
                  <button
                    type="button"
                    onClick={()=> setAccordion(!accordion)}
                    className={[
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500',
                      accordion ? 'bg-purple-600' : 'bg-slate-300'
                    ].join(' ')}
                    aria-pressed={accordion}
                    aria-label="Toggle accordion navigation"
                  >
                    <span className={[
                      'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                      accordion ? 'translate-x-5' : 'translate-x-0.5'
                    ].join(' ')} />
                  </button>
                </div>
              </div>
              <button data-profile-item onClick={profileActions.profile} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:bg-slate-100 focus:outline-none" role="menuitem">Profile</button>
              <button data-profile-item onClick={profileActions.settings} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:bg-slate-100 focus:outline-none" role="menuitem">Settings</button>
              <div className="my-1 h-px bg-slate-100" />
              <button data-profile-item onClick={profileActions.logout} className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 focus:bg-rose-50 focus:outline-none" role="menuitem">Logout</button>
            </div>
          )}
        </div>
        <CommandPalette
          open={open}
          onClose={()=> setOpen(false)}
          departments={filteredDepts}
          menus={filteredMenusMap}
          onAction={(item)=>{
            switch(item.id){
              case 'cmd:toggle-compact':
                setCompact(!compact); break;
              case 'cmd:start-trust-scan':
                navigate('/trust-tech-scan/run');
                notifAdd({ title:'Scan Wizard', body:'Opened Trust Tech Scan run workflow.' });
                break;
              case 'cmd:start-report':
                navigate('/reports/create');
                notifAdd({ title:'Report Wizard', body:'Opened Create Report workflow.' });
                break; // adjust path if different
              case 'cmd:open-notifications':
                setNotifOpen(true); break;
              case 'cmd:open-profile':
                setProfileOpen(true); break;
              case 'cmd:go-dashboard':
                navigate('/trust-tech-studio'); break;
              case 'cmd:open-todos':
                navigate('/experience-enablement/todos'); break;
              default:
                break;
            }
          }}
        />
      </div>
    </header>
  )
}

// Delayed tooltip for compact sidebar labels
function DelayedTooltip({ label }){
  const [show, setShow] = React.useState(false);
  const ref = React.useRef({ timer:null, hover:false });
  function enter(){
    ref.current.hover = true;
    ref.current.timer = setTimeout(()=>{ if(ref.current.hover) setShow(true); }, 400);
  }
  function leave(){
    ref.current.hover = false; clearTimeout(ref.current.timer); setShow(false);
  }
  return (
    <span
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
      className={"pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 text-white text-xs px-2 py-1 shadow-lg transition-all " + (show ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2')}
    >{label}</span>
  );
}

